import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BucketSettings, SmartStatementBucket, SmartStatementBucketSetting } from '@anduin/api';
import { LaunchDarklyFlag, LaunchDarklyState } from '@anduin/auth';
import { CollectionsMode, ConfigService } from '@anduin/config';
import { distinctUntilObjectChanged, getControlValueChanges, ignoreEmpty, mapToAndResult, mapToOrResult } from '@anduin/shared';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, shareReplay, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { InputWithVariableComponent } from '../input-with-variable/input-with-variable.component';

interface SmartStatementBucketSettingDisplay {
  bucket: SmartStatementBucketSetting;
  form: UntypedFormGroup;

  headerPlaceholder$: Observable<string>;
  subjectPlaceholder$: Observable<string>;
  bodyPlaceholder$: Observable<string>;
  hasCustomOverride$: Observable<boolean>;
}

interface SmartStatementBucketSettingDisplayForm {
  header: string;
  subject: string;
  body: string;
}

type BridgedBucketSettings = SmartStatementBucketSetting | BucketSettings;

@Component({
  selector: 'app-collections-bucket-message-form',
  templateUrl: './collections-bucket-message-form.component.html',
  styleUrls: ['./collections-bucket-message-form.component.scss'],
  standalone: false,
})
export class CollectionsBucketMessageFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren(InputWithVariableComponent) inputs: QueryList<InputWithVariableComponent>;

  private _unsubscribe$: Subject<void> = new Subject<void>();

  private _buckets$ = new BehaviorSubject<BridgedBucketSettings[]>([]);
  buckets$ = this._buckets$.pipe(distinctUntilObjectChanged());

  @Input() set buckets(newBuckets: BridgedBucketSettings[]) {
    this._buckets$.next(newBuckets);
  }
  get buckets(): BridgedBucketSettings[] {
    return this._buckets$.getValue();
  }

  @Input() messageVariables: Map<string, string> = new Map<string, string>();

  private _disabled = false;

  @Input()
  set disabled(value: boolean) {
    this._disabled = value;
    // Trigger a bucket update to apply disabled state to forms
    this._buckets$.next(this._buckets$.getValue());
  }

  get disabled(): boolean {
    return this._disabled;
  }

  @Output() bucketsChange: EventEmitter<BridgedBucketSettings[]> = new EventEmitter<BridgedBucketSettings[]>();
  @Output() isValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  bucketDisplays$: Observable<SmartStatementBucketSettingDisplay[]> = this.buckets$.pipe(
    map((buckets) =>
      buckets.reduce((displays, bucket) => {
        const precedingBucket = displays.find((displayBucket) => displayBucket.bucket.ordinal === bucket.ordinal - 1);
        const form: UntypedFormGroup = new UntypedFormGroup({
          header: new UntypedFormControl(!bucket.isHeaderInherited ? bucket.header : null),
          subject: new UntypedFormControl(!bucket.isSubjectInherited ? bucket.subject : null),
          body: new UntypedFormControl(!bucket.isBodyInherited ? bucket.body : null),
        });

        // 0-30 days bucket requires all fields to be filled (following buckets can then inherit if the corresponding field is left blank)
        if (bucket.smartStatementBucket === SmartStatementBucket.ZERO_TO_THIRTY) {
          form.controls.header.setValidators(Validators.required);
          form.controls.subject.setValidators(Validators.required);
          form.controls.body.setValidators(Validators.required);
        }

        // Apply disabled state
        if (this._disabled) {
          form.disable();
        }

        return [
          ...displays,
          {
            bucket,
            form,

            hasCustomOverride$: combineLatest(
              [form.controls.header, form.controls.subject, form.controls.body].map((control) => getControlValueChanges<string>(control)),
            ).pipe(mapToOrResult()),
            headerPlaceholder$: this.getPlaceholder$(
              bucket.isHeaderInherited,
              bucket.header,
              precedingBucket?.form?.controls?.header,
              precedingBucket?.headerPlaceholder$,
            ),
            subjectPlaceholder$: this.getPlaceholder$(
              bucket.isSubjectInherited,
              bucket.subject,
              precedingBucket?.form?.controls?.subject,
              precedingBucket?.subjectPlaceholder$,
            ),
            bodyPlaceholder$: this.getPlaceholder$(
              bucket.isBodyInherited,
              bucket.body,
              precedingBucket?.form?.controls?.body,
              precedingBucket?.bodyPlaceholder$,
            ),
          } as SmartStatementBucketSettingDisplay,
        ];
      }, [] as SmartStatementBucketSettingDisplay[]),
    ),
    shareReplay(1),
  );

  getPlaceholder$(
    isInherited: boolean,
    existingValue: string,
    precedingBucketFieldControl: AbstractControl,
    placeholder$: Observable<string>,
  ): Observable<string> {
    if (!precedingBucketFieldControl) {
      return of(isInherited ? existingValue : '');
    }

    return combineLatest([placeholder$, getControlValueChanges<string>(precedingBucketFieldControl)]).pipe(
      map(([placeholder, value]) => (value ? value : placeholder)),
    );
  }

  recipientLabel$ = this.store
    .select(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2))
    .pipe(map((enabled) => (!enabled || this.configService.collectionsMode === CollectionsMode.CLIENT ? 'customer' : 'billing contact')));

  constructor(
    private readonly store: Store,
    private readonly configService: ConfigService,
  ) {}

  ngOnInit(): void {
    this.bucketDisplays$
      .pipe(
        switchMap((displays) =>
          combineLatest([
            ...displays.map((display) =>
              getControlValueChanges<SmartStatementBucketSettingDisplayForm>(display.form).pipe(
                map((value) => ({ ...display.bucket, ...value }) as BridgedBucketSettings),
              ),
            ),
          ]),
        ),
        debounceTime(100),
        tap((updatedBuckets) => this.bucketsChange.emit(updatedBuckets)),
        takeUntil(this._unsubscribe$),
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    // Update the parent component about validity
    combineLatest([
      // Outer controls valid:
      this.bucketDisplays$.pipe(
        // Emit the status of any form input if the form status changes
        switchMap((displays) =>
          combineLatest(
            displays.map((display) =>
              display.form.statusChanges.pipe(
                startWith(display.form.status),
                map((status) => status === 'VALID'),
              ),
            ),
          ),
        ),
        // Make sure that all form controls are valid
        mapToAndResult(),
      ),

      // Inner controls valid:
      this.inputs.changes.pipe(
        startWith(this.inputs),
        ignoreEmpty(),
        // Monitor if the inputs with variables are valid and summate the result
        switchMap((_) => combineLatest(this.inputs.map((input) => input.isValid.pipe(startWith(true))))),
        mapToAndResult(),
        distinctUntilChanged(),
      ),
    ])
      .pipe(
        mapToAndResult(),
        tap((isValid) => this.isValid.emit(isValid)),
        takeUntil(this._unsubscribe$),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  resetBuckets(): void {
    this.bucketDisplays$
      .pipe(
        take(1),
        tap((bucketDisplays) =>
          bucketDisplays.forEach((display) => {
            display.form.controls.header.setValue(!display.bucket.isHeaderInherited ? display.bucket.header : null);
            display.form.controls.subject.setValue(!display.bucket.isSubjectInherited ? display.bucket.subject : null);
            display.form.controls.body.setValue(!display.bucket.isBodyInherited ? display.bucket.body : null);
          }),
        ),
      )
      .subscribe();
  }

  readonly SmartStatementBucket = SmartStatementBucket;

  identify(_index: number, display: SmartStatementBucketSettingDisplay) {
    return display.bucket?.name;
  }
}
